import comingSoonBanner from "assets/icons/comingSoonBanner.svg";
import poweredByIcon from "assets/icons/powered_by.svg";
import StoreImageFallback from "components/atoms/StoreImageFallback";
import Text from "components/atoms/text";
import ClosedView from "components/organisms/ClosedView";
import config from "config/config";
import {
  FILTER_COMPONENT_ID,
  GET_STORES_REFRESH_TIME,
  PLATFORM,
  STORES_CACHE_TIME,
} from "config/envConfig";
import { useConfig } from "context/configContext";
import { DeliveryOptionContext } from "context/deliveryOptionContext";
import { useNav } from "context/navContext";
import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import apiList from "services/apiList";
import { callApi } from "services/callApi";
import {
  ALLOW_ALL_STORES_KEY,
  DATA_REFERENCE,
  STORE_LIST_KEY,
  MOVEMENT_KEY,
  SECTOR_KEY,
  TERMINAL_KEY,
} from "utils/Constants";
import { getCachedValue, setCachedValue } from "utils/cacheUtil";
import { triggerStoreViewed } from "utils/cdp/Common";
import { ClosedStoreIcon } from "utils/iconList";
import moment from "utils/momentWrapper";

export const StartOrder = () => {
  const history = useHistory();
  const { config: appConfig, timezone } = useConfig();
  const useDelivery = useContext(DeliveryOptionContext);
  const { setShowHeader, setExcludedInActivityRoutes } = useNav();

  const [loading, setLoading] = useState(true);
  const [storesList, setStoreList] = useState([]);

  const getStores = async (isInterval = false) => {
    try {
      if (!isInterval) setLoading(true);

      let apiURL = `${apiList.stores.getStores}`;
      const data = JSON.parse(localStorage.getItem(DATA_REFERENCE));

      const location = data && data[TERMINAL_KEY] ? data[TERMINAL_KEY] : "";
      const sector = data && data[SECTOR_KEY] ? data[SECTOR_KEY] : "";
      const section = data && data[MOVEMENT_KEY] ? data[MOVEMENT_KEY] : "";

      const cacheKey = `stores:${location}:${sector}:${section}:${
        useDelivery.selectedDeliveryOption?.deliveryOption || ""
      }:${appConfig?.domain || ""}`;

      const cached = getCachedValue(cacheKey);
      const allowAllCached =
        data && data[ALLOW_ALL_STORES_KEY] !== undefined
          ? data[ALLOW_ALL_STORES_KEY]
          : true;
      const allowedStoreIdsCached =
        data && Array.isArray(data[STORE_LIST_KEY]) ? data[STORE_LIST_KEY] : [];

      if (cached) {
        const filteredFromCache =
          allowAllCached || allowedStoreIdsCached.length === 0
            ? cached
            : cached.filter((store) =>
                allowedStoreIdsCached.includes(store._id)
              );
        setStoreList(filteredFromCache);
        if (!isInterval) setLoading(false);
      }

      const response = await callApi.get(apiURL, {
        isRetail: true,
        location: location,
        sector: sector,
        section: section,
        supportedFulfillmentTypes:
          useDelivery.selectedDeliveryOption?.deliveryOption || "",
        orderDateTime: moment(new Date(), "UTC", "UTC").format(),
        domain: appConfig?.domain,
        timezone: timezone,
        platform: PLATFORM,
      });
      if (response.status === 200) {
        let completeStores = response?.data?.indoor?.concat(
          response?.data?.outdoor
        );
        const sortedStores = completeStores.sort((a, b) =>
          a.storeDisplayName.localeCompare(b.storeDisplayName)
        );

        setCachedValue(cacheKey, sortedStores, STORES_CACHE_TIME);

        const allowAll =
          data && data[ALLOW_ALL_STORES_KEY] !== undefined
            ? data[ALLOW_ALL_STORES_KEY]
            : true;
        const allowedStoreIds =
          data && Array.isArray(data[STORE_LIST_KEY])
            ? data[STORE_LIST_KEY]
            : [];

        const filteredStores =
          allowAll || allowedStoreIds.length === 0
            ? sortedStores
            : sortedStores.filter((store) =>
                allowedStoreIds.includes(store._id)
              );

        setStoreList(filteredStores);
      } else {
        setStoreList([]);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStores();

    setShowHeader(false);
    return () => {
      setShowHeader(true);
    };
  }, []);

  useEffect(() => {
    const getStorePollingId = setInterval(() => {
      getStores(true);
    }, GET_STORES_REFRESH_TIME);

    return () => clearInterval(getStorePollingId);
  }, [storesList]);

  useEffect(() => {
    const newExcluded =
      storesList && storesList.length > 0 ? ["/"] : ["/", "/start"];

    setExcludedInActivityRoutes((prev) => {
      const isSame =
        prev.length === newExcluded.length &&
        prev.every((r) => newExcluded.includes(r));

      return isSame ? prev : newExcluded;
    });
  }, [storesList]);

  const getShopNameIcon = (store) => {
    const thumbnail = store?.shopImage?.find(
      (image) =>
        image.imageType === "shopNameLogo" && image.platform === PLATFORM
    );

    return thumbnail?.imageURL;
  };

  const getShopImage = (store) => {
    const thumbnail = store?.shopImage?.find(
      (image) => image.imageType === "medium" && image.platform === PLATFORM
    );

    return thumbnail?.imageURL || store?.shopBrandImageUrl;
  };

  const handleStoreSelection = (store) => {
    if (store?.showComingSoonBanner) return;
    let selectedDeliveryOption = {
      deliveryOption: config.deliveryOptions.takeaway,
      deliveryAddress: "",
      isPackagingRequested: true,
      deliveryTime: moment(new Date(), "UTC", "UTC").format(),
      timezone: timezone,
    };
    useDelivery.handleDeliverySelection(selectedDeliveryOption);

    triggerStoreViewed({
      store_id: store?._id,
      store_name: store?.storeDisplayName,
      store_location: store?.location || store?.terminal?.displayLabel || "",
      terminal: store?.terminal?.value,
      sector: store?.sector?.value,
      movement_type: store?.movementType?.value,
    });

    const now = moment(new Date(), timezone);

    const currentDay = now.format("ddd");
    const currentMinutes = now.hours() * 60 + now.minutes();

    for (const timing of store.shopTimings) {
      if (timing.dayOfWeek.includes(currentDay)) {
        const startMinutes =
          parseInt(timing.startHour) * 60 + parseInt(timing.startMin);
        const endMinutes =
          parseInt(timing.endHour) * 60 + parseInt(timing.endMin);

        const isOpen =
          startMinutes <= currentMinutes && currentMinutes <= endMinutes;

        history.push(
          `/products/${store._id}?filterComponentId=${FILTER_COMPONENT_ID}&isOpen=${isOpen}`
        );
      }
    }
  };

  if (!loading && storesList && storesList.length === 0) {
    return <ClosedView />;
  }

  return (
    <div className="w-[calc(100% - 128px)] h-screen bg-accent-to-secondary flex flex-col items-center justify-center gap-20 px-16">
      <h2
        className="text-center font-secondary px-16 my-0"
        style={{
          fontWeight: "700",
          fontSize: "68px",
          lineHeight: "91px",
          letterSpacing: "0em",
        }}
      >
        Combine your favourites from all the restaurants in the food court
      </h2>
      <div className="flex flex-row gap-12 px-10 font-primary">
        <span className="text-3xl text-center flex-1 font-medium">
          1. <br /> Choose a restaurant
        </span>
        <span className="text-3xl text-center flex-1 font-medium">
          2. <br /> Combine favourites across restaurants
        </span>
        <span className="text-3xl text-center flex-1 font-medium">
          3. <br /> Pay and get notified via SMS
        </span>
      </div>
      <div className="grid grid-cols-3 grid-rows-2 gap-12">
        {storesList &&
          storesList.length > 0 &&
          storesList?.map((store) => {
            return (
              <div
                key={store._id}
                className="flex flex-col items-center bg-secondary w-[272px] h-[276px] rounded-[12px]"
                onClick={() => {
                  handleStoreSelection(store);
                }}
              >
                <div className="relative w-full">
                  <img
                    src={getShopImage(store)}
                    alt={store.storeDisplayName}
                    style={{
                      borderRadius: "12px 12px 0px 0px",
                    }}
                  />
                  {store?.showComingSoonBanner && (
                    <img
                      src={comingSoonBanner}
                      alt="Coming soon"
                      className="absolute top-2 right-2"
                    />
                  )}
                </div>
                <div
                  className="justify-center items-start flex flex-col gap-4 mx-6 font-primary h-full"
                  style={{
                    width: "calc(100% - 48px)",
                  }}
                >
                  {/* <p className="text-3xl font-bold line-clamp-1">
                      {store.storeDisplayName}
                    </p> */}
                  <StoreImageFallback
                    src={getShopNameIcon(store)}
                    alt={store.storeDisplayName}
                    fallbackText={store.storeDisplayName}
                    imageStyle={{
                      maxHeight: "182px",
                      maxWidth: "100%",
                    }}
                    fallbackTextStyle={{
                      fontWeight: "700",
                      fontSize: "24px",
                      lineHeight: "30px",
                      color: "var(--secondary-foreground)",
                    }}
                  />
                  <p
                    style={{
                      fontWeight: "400",
                      fontSize: "14px",
                      lineHeight: "18px",
                      letterSpacing: "0em",
                      color: "var(--secondary-foreground)",
                    }}
                  >
                    {store.shopCategory?.values?.[0]?.name}
                  </p>
                </div>
              </div>
            );
          })}
      </div>
      <img
        src={poweredByIcon}
        style={{
          position: "fixed",
          bottom: "60px",
          right: "60px",
        }}
      />
      {/* <Button
        height="160px"
        width="50%"
        className="rounded-2xl text-4xl font-bold"
        onClick={() => {
          history.push({ pathname: "/homepage" });
        }}
      >
        Start Your Order
      </Button> */}
    </div>
  );
};
